import { useState } from "react";
import clsx from "clsx";

import type { ProjectSchema, BudgetSchema, DepartmentSchema } from "@nexttrack/api-schema";
import {
    useGetProjectsQuery,
    useCreateProjectMutation,
    useUpdateProjectMutation,
    useDeleteProjectMutation,
    useAddBudgetMutation,
    useUpdateBudgetMutation,
    useDeleteBudgetMutation,
} from "@/store/services/projects/projects.service";
import { useGetDepartmentsQuery } from "@/store/services/departments/departments.service";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

type BudgetUnit = "fte" | "man_month" | "man_day";
type CommitmentInput = { departmentId: string; value: string };

const countMonths = (start: string, end: string) => {
    const [sy, sm] = start.split("-").map(Number);
    const [ey, em] = end.split("-").map(Number);
    return (ey - sy) * 12 + (em - sm) + 1;
};

const toCommittedManDays = (value: number, unit: BudgetUnit, ratio: number, n: number): number => {
    if (unit === "man_day") return value;
    if (unit === "man_month") return value * ratio;
    return value * ratio * n;
};

const toDisplayValue = (committedMd: number, unit: BudgetUnit, ratio: number, n: number): number => {
    if (unit === "man_day") return committedMd;
    if (unit === "man_month") return ratio > 0 ? committedMd / ratio : 0;
    return ratio > 0 && n > 0 ? committedMd / (ratio * n) : 0;
};

const formatServiceValue = (committedMd: number, bp: BudgetSchema): string => {
    const n = countMonths(bp.startDate, bp.endDate);
    if (bp.unit === "man_day") return `${committedMd.toFixed(1)} MD`;
    if (bp.unit === "man_month") {
        const mm = bp.manDaysPerManMonth > 0 ? committedMd / bp.manDaysPerManMonth : 0;
        return `${mm.toFixed(2)} MM`;
    }
    const fte = bp.manDaysPerManMonth > 0 && n > 0 ? committedMd / (bp.manDaysPerManMonth * n) : 0;
    return `${fte.toFixed(2)} FTE/mo`;
};

export const ProjectsPage = () => {
    const { data: projects, isLoading } = useGetProjectsQuery();
    const { data: departments } = useGetDepartmentsQuery();
    const [createProject, { isLoading: isCreating }] = useCreateProjectMutation();
    const [updateProject, { isLoading: isUpdating }] = useUpdateProjectMutation();
    const [deleteProject] = useDeleteProjectMutation();
    const [addBudget] = useAddBudgetMutation();
    const [updateBudget] = useUpdateBudgetMutation();
    const [deleteBudget] = useDeleteBudgetMutation();

    const [expandedId, setExpandedId] = useState<string | null>(null);

    // New project dialog
    const [newOpen, setNewOpen] = useState(false);
    const [newName, setNewName] = useState("");

    // Edit project dialog
    const [editTarget, setEditTarget] = useState<ProjectSchema | null>(null);
    const [editName, setEditName] = useState("");
    const [editArchived, setEditArchived] = useState(false);

    // Budget dialog
    const [budgetOpen, setBudgetOpen] = useState(false);
    const [budgetTarget, setBudgetTarget] = useState<{ projectId: string; period?: BudgetSchema } | null>(null);
    const [budgetName, setBudgetName] = useState("");
    const [budgetStartDate, setBudgetStartDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [budgetEndDate, setBudgetEndDate] = useState(() => {
        const d = new Date();
        d.setMonth(d.getMonth() + 6);
        return d.toISOString().slice(0, 10);
    });
    const [budgetUnit, setBudgetUnit] = useState<BudgetUnit>("fte");
    const [manDaysPerManMonth, setManDaysPerManMonth] = useState("20");
    const [commitments, setCommitments] = useState<CommitmentInput[]>([]);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newName.trim()) return;
        await createProject({ name: newName.trim() });
        setNewName("");
        setNewOpen(false);
    };

    const openEdit = (p: ProjectSchema) => {
        setEditTarget(p);
        setEditName(p.name);
        setEditArchived(p.archived);
    };

    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editTarget) return;
        await updateProject({
            id: editTarget.id,
            data: { name: editName.trim(), archived: editArchived },
        });
        setEditTarget(null);
    };

    const handleDelete = async (id: string) => {
        if (!confirm("Delete this project?")) return;
        await deleteProject(id);
        if (expandedId === id) setExpandedId(null);
    };

    const openBudget = (projectId: string, period?: BudgetSchema) => {
        setBudgetTarget({ projectId, period });
        setBudgetName(period?.name ?? "");
        setBudgetStartDate(period?.startDate ?? new Date().toISOString().slice(0, 10));
        const defaultEnd = new Date();
        defaultEnd.setMonth(defaultEnd.getMonth() + 6);
        const startDate = period?.startDate ?? new Date().toISOString().slice(0, 10);
        const endDate = period?.endDate ?? defaultEnd.toISOString().slice(0, 10);
        setBudgetEndDate(endDate);

        const unit: BudgetUnit = period?.unit ?? "fte";
        const ratio = period?.manDaysPerManMonth ?? 20;
        setBudgetUnit(unit);
        setManDaysPerManMonth(String(ratio));

        if (period) {
            const n = countMonths(startDate, endDate);
            setCommitments(
                period.services.map((c) => ({
                    departmentId: c.departmentId,
                    value: String(parseFloat(toDisplayValue(c.committedManDays, unit, ratio, n).toFixed(4))),
                })),
            );
        } else {
            setCommitments([]);
        }
        setBudgetOpen(true);
    };

    const handleUnitChange = (newUnit: BudgetUnit) => {
        const ratio = parseFloat(manDaysPerManMonth) || 20;
        const n = countMonths(budgetStartDate, budgetEndDate);
        setCommitments((prev) =>
            prev.map((c) => {
                const val = parseFloat(c.value);
                if (isNaN(val) || c.value === "") return c;
                const md = toCommittedManDays(val, budgetUnit, ratio, n);
                const newVal = toDisplayValue(md, newUnit, ratio, n);
                return { ...c, value: String(parseFloat(newVal.toFixed(4))) };
            }),
        );
        setBudgetUnit(newUnit);
    };

    const handleBudgetSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!budgetTarget) return;

        const ratio = parseFloat(manDaysPerManMonth) || 20;
        const n = countMonths(budgetStartDate, budgetEndDate);

        const parsedCommitments = commitments
            .filter((c) => c.departmentId && c.value !== "")
            .map((c) => ({
                departmentId: c.departmentId,
                committedManDays: toCommittedManDays(parseFloat(c.value), budgetUnit, ratio, n),
            }));

        const data = {
            name: budgetName.trim(),
            startDate: budgetStartDate,
            endDate: budgetEndDate,
            unit: budgetUnit,
            manDaysPerManMonth: ratio,
            commitments: parsedCommitments,
        };
        if (budgetTarget.period) {
            await updateBudget({
                projectId: budgetTarget.projectId,
                budgetId: budgetTarget.period.id,
                data,
            });
        } else {
            await addBudget({ projectId: budgetTarget.projectId, data });
        }
        setBudgetOpen(false);
        setBudgetTarget(null);
    };

    const handleDeleteBudget = async (projectId: string, budgetId: string) => {
        if (!confirm("Delete this budget?")) return;
        await deleteBudget({ projectId, budgetId });
    };

    const addCommitmentRow = () => {
        setCommitments((prev) => [...prev, { departmentId: "", value: "" }]);
    };

    const updateCommitment = (index: number, field: keyof CommitmentInput, value: string) => {
        setCommitments((prev) => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
    };

    const removeCommitment = (index: number) => {
        setCommitments((prev) => prev.filter((_, i) => i !== index));
    };

    const getDeptName = (depts: DepartmentSchema[] | undefined, id: string) =>
        depts?.find((d) => d.id === id)?.name ?? id;

    const unitLabels: Record<BudgetUnit, string> = { fte: "FTE", man_month: "Man-month", man_day: "Man-day" };
    const unitPlaceholders: Record<BudgetUnit, string> = {
        fte: "e.g. 1.5",
        man_month: "e.g. 13.5",
        man_day: "e.g. 270",
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">Projects</h1>
                <Button onClick={() => setNewOpen(true)}>New Project</Button>
            </div>

            {isLoading ? (
                <div className="space-y-2">
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-10 w-full" />
                    ))}
                </div>
            ) : (
                <div className="border rounded-lg overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {projects?.map((project) => {
                                const isExpanded = expandedId === project.id;
                                return (
                                    <>
                                        <TableRow
                                            key={project.id}
                                            className={clsx("cursor-pointer", isExpanded && "bg-slate-50")}
                                            onClick={() => setExpandedId(isExpanded ? null : project.id)}
                                        >
                                            <TableCell className="font-medium">
                                                <span
                                                    className={clsx(project.archived && "line-through text-slate-400")}
                                                >
                                                    {project.name}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {project.archived ? (
                                                    <Badge variant="secondary">Archived</Badge>
                                                ) : (
                                                    <Badge variant="outline">Active</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell
                                                className="text-right space-x-2"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Button variant="outline" size="sm" onClick={() => openEdit(project)}>
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="destructive"
                                                    size="sm"
                                                    onClick={() => handleDelete(project.id)}
                                                >
                                                    Delete
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                        {isExpanded && (
                                            <TableRow key={`${project.id}-expanded`}>
                                                <TableCell colSpan={3} className="bg-slate-50 p-4">
                                                    <div className="space-y-3">
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm font-semibold">Budgets</span>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => openBudget(project.id)}
                                                            >
                                                                Add Budget
                                                            </Button>
                                                        </div>
                                                        {project.budgets.length === 0 ? (
                                                            <p className="text-xs text-slate-400">
                                                                No budgets defined.
                                                            </p>
                                                        ) : (
                                                            <div className="space-y-3">
                                                                {project.budgets
                                                                    .slice()
                                                                    .sort((a, b) =>
                                                                        a.startDate.localeCompare(b.startDate),
                                                                    )
                                                                    .map((bp) => {
                                                                        return (
                                                                            <div
                                                                                key={bp.id}
                                                                                className="border rounded p-3 bg-white text-xs space-y-2"
                                                                            >
                                                                                <div className="flex items-center justify-between">
                                                                                    <span className="font-medium text-slate-700">
                                                                                        {bp.name}
                                                                                        <span className="ml-2 text-slate-400 font-normal">
                                                                                            {bp.startDate} —{" "}
                                                                                            {bp.endDate}
                                                                                            <span>
                                                                                                {" "}
                                                                                                ({
                                                                                                    bp.manDaysPerManMonth
                                                                                                }{" "}
                                                                                                d/mo)
                                                                                            </span>
                                                                                        </span>
                                                                                    </span>
                                                                                    <div className="flex gap-1">
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            onClick={() =>
                                                                                                openBudget(
                                                                                                    project.id,
                                                                                                    bp,
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            Edit
                                                                                        </Button>
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            className="text-red-500"
                                                                                            onClick={() =>
                                                                                                handleDeleteBudget(
                                                                                                    project.id,
                                                                                                    bp.id,
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            Del
                                                                                        </Button>
                                                                                    </div>
                                                                                </div>
                                                                                {bp.services.length === 0 ? (
                                                                                    <p className="text-slate-400">
                                                                                        No services.
                                                                                    </p>
                                                                                ) : (
                                                                                    <div className="space-y-3">
                                                                                        {bp.services.map((c) => {
                                                                                            const remaining =
                                                                                                c.committedManDays -
                                                                                                c.bookedManDays;
                                                                                            const fillPct =
                                                                                                c.committedManDays > 0
                                                                                                    ? Math.min(
                                                                                                          100,
                                                                                                          (c.bookedManDays /
                                                                                                              c.committedManDays) *
                                                                                                              100,
                                                                                                      )
                                                                                                    : 100;
                                                                                            return (
                                                                                                <div
                                                                                                    key={c.id}
                                                                                                    className="border-t pt-2 space-y-1"
                                                                                                >
                                                                                                    <div className="flex items-center justify-between text-sm">
                                                                                                        <span className="font-medium">
                                                                                                            {getDeptName(
                                                                                                                departments,
                                                                                                                c.departmentId,
                                                                                                            )}
                                                                                                        </span>
                                                                                                        <span className="text-slate-500">
                                                                                                            {formatServiceValue(
                                                                                                                c.committedManDays,
                                                                                                                bp,
                                                                                                            )}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                    <div className="flex justify-between text-xs text-slate-500">
                                                                                                        <span>
                                                                                                            {c.bookedManDays.toFixed(
                                                                                                                1,
                                                                                                            )}{" "}
                                                                                                            MD booked
                                                                                                        </span>
                                                                                                        <span>
                                                                                                            {c.committedManDays.toFixed(
                                                                                                                1,
                                                                                                            )}{" "}
                                                                                                            MD committed
                                                                                                        </span>
                                                                                                    </div>
                                                                                                    <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                                                                                                        <div
                                                                                                            className={clsx(
                                                                                                                "h-2 rounded-full transition-all",
                                                                                                                remaining >=
                                                                                                                    0
                                                                                                                    ? "bg-blue-500"
                                                                                                                    : "bg-red-500",
                                                                                                            )}
                                                                                                            style={{
                                                                                                                width: `${fillPct}%`,
                                                                                                            }}
                                                                                                        />
                                                                                                    </div>
                                                                                                    <div className="flex justify-end">
                                                                                                        <span
                                                                                                            className={clsx(
                                                                                                                "text-xs font-semibold",
                                                                                                                remaining ===
                                                                                                                    0
                                                                                                                    ? "text-green-600"
                                                                                                                    : remaining >
                                                                                                                        0
                                                                                                                      ? "text-amber-600"
                                                                                                                      : "text-red-600",
                                                                                                            )}
                                                                                                        >
                                                                                                            {remaining >=
                                                                                                            0
                                                                                                                ? `${remaining.toFixed(1)} MD remaining`
                                                                                                                : `${Math.abs(remaining).toFixed(1)} MD over`}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                </div>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </>
                                );
                            })}
                            {(!projects || projects.length === 0) && (
                                <TableRow>
                                    <TableCell colSpan={3} className="text-center text-slate-400 py-8">
                                        No projects yet. Create one to get started.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* New Project Dialog */}
            <Dialog open={newOpen} onOpenChange={setNewOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>New Project</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Name</label>
                            <Input value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setNewOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isCreating || !newName.trim()}>
                                {isCreating ? "Creating..." : "Create"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit Project Dialog */}
            <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Project</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleUpdate} className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Name</label>
                            <Input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
                        </div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={editArchived} onCheckedChange={(v) => setEditArchived(Boolean(v))} />
                            Archived
                        </label>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isUpdating || !editName.trim()}>
                                {isUpdating ? "Saving..." : "Save"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Budget Dialog */}
            <Dialog
                open={budgetOpen}
                onOpenChange={(open) => {
                    setBudgetOpen(open);
                    if (!open) setBudgetTarget(null);
                }}
            >
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{budgetTarget?.period ? "Edit Budget" : "Add Budget"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleBudgetSave} className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Name</label>
                            <Input
                                value={budgetName}
                                onChange={(e) => setBudgetName(e.target.value)}
                                placeholder="e.g. Budget 2026"
                                autoFocus
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-sm font-medium mb-1 block">Start Date</label>
                                <Input
                                    type="date"
                                    value={budgetStartDate}
                                    onChange={(e) => setBudgetStartDate(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1 block">End Date</label>
                                <Input
                                    type="date"
                                    value={budgetEndDate}
                                    onChange={(e) => setBudgetEndDate(e.target.value)}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">Input unit</label>
                            <div className="flex rounded-md border overflow-hidden">
                                {(["fte", "man_month", "man_day"] as BudgetUnit[]).map((u) => (
                                    <button
                                        key={u}
                                        type="button"
                                        className={clsx(
                                            "flex-1 px-3 py-1.5 text-sm font-medium transition-colors",
                                            budgetUnit === u
                                                ? "bg-slate-900 text-white"
                                                : "bg-white text-slate-700 hover:bg-slate-50",
                                        )}
                                        onClick={() => handleUnitChange(u)}
                                    >
                                        {unitLabels[u]}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">Working days / man-month</label>
                            <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={manDaysPerManMonth}
                                onChange={(e) => setManDaysPerManMonth(e.target.value)}
                            />
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium">Services ({unitLabels[budgetUnit]})</label>
                                <Button type="button" variant="outline" size="sm" onClick={addCommitmentRow}>
                                    + Add
                                </Button>
                            </div>
                            {commitments.length === 0 && <p className="text-xs text-slate-400">No services added.</p>}
                            <div className="space-y-2">
                                {commitments.map((c, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                        <select
                                            className="flex-1 border rounded-md px-3 py-1.5 text-sm bg-white"
                                            value={c.departmentId}
                                            onChange={(e) => updateCommitment(i, "departmentId", e.target.value)}
                                        >
                                            <option value="">Select department…</option>
                                            {departments?.map((d) => (
                                                <option key={d.id} value={d.id}>
                                                    {d.name}
                                                </option>
                                            ))}
                                        </select>
                                        <Input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            placeholder={unitPlaceholders[budgetUnit]}
                                            className="w-28"
                                            value={c.value}
                                            onChange={(e) => updateCommitment(i, "value", e.target.value)}
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-500 px-2"
                                            onClick={() => removeCommitment(i)}
                                        >
                                            ×
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setBudgetOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={!budgetName.trim()}>
                                Save
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};
